/**
 * Core robot abstraction types and interfaces
 */

import { Machine } from '../api/graphql/generated';
import {
  TeachIOMapping,
  ToolConfig,
} from '../scratchpad/application/shared/model/flow.types';
import { QNear, Waypoint } from '../util/machine-variable.utils';

// Re-export types for convenience
export type { Machine, TeachIOMapping };
export type Pose = [number, number, number, number, number, number];

export interface JointDHParameters {
  theta: number;
  a: number;
  d: number;
  alpha: number;
}

/**
 * Supported robot manufacturers
 */
export enum RobotManufacturer {
  FANUC = 'fanuc',
  UNIVERSAL_ROBOTS = 'universal_robots',
}

/**
 * Universal Robots required context
 */
export type URContext = {
  toolConfig: ToolConfig;
};

/**
 * Fanuc required context
 */
export type BeaconEngineContext = {
  toolConfig: ToolConfig;
};

/**
 * Union of all manufacturer-specific contexts
 */
export type ImplementationContext = URContext | BeaconEngineContext;
export type ManufacturerNameArg = RobotManufacturer | string | null | undefined;

/**
 * Script generation context
 */
export interface RobotOperationalContext {
  /** Executing step index for live execution tracking */
  executingIndex: number;
  /** Additional context-specific parameters */
  [key: string]: any;
}

/**
 * Robot manufacturer interface
 */
export interface ManufacturerPlugin<RobotModel = string> {
  /**
   * Get the manufacturer this plugin supports.
   * Example: 'universal_robots'
   */
  getManufacturer(): RobotManufacturer;

  /**
   * Get the display name of the manufacturer.
   * Example: "Universal Robots"
   */
  getManufacturerDisplayName(): string;

  /** Get all supported models for this manufacturer */
  getModels(): RobotModel[];

  /** Get model by model number/identifier */
  getModelByNumber(modelNumber?: string | null): RobotModel | undefined;

  /** Check if a model number is valid for this manufacturer */
  isValidModel(modelNumber?: string | null): boolean;

  /** Get kinematics calculator for this manufacturer */
  getKinematics(modelNumber?: string | null): RobotKinematics | undefined;

  /** Get default IO mapping for this manufacturer */
  getDefaultIOMapping(machine: Machine): TeachIOMapping;
}

/**
 * Interface for the robot registry service that manages manufacturer-specific
 * robot functionality through a plugin architecture.
 */
export interface RobotRegistryService {
  /**
   * Register a manufacturer plugin with the registry.
   */
  registerPlugin(plugin: ManufacturerPlugin): void;

  /**
   * Get a registered plugin for a manufacturer.
   */
  getPlugin(
    manufacturerName: ManufacturerNameArg
  ): ManufacturerPlugin | undefined;

  /**
   * Get all registered manufacturers.
   */
  getManufacturers(): RobotManufacturer[];

  /**
   * Get all models for a manufacturer.
   */
  getModels(manufacturerName: ManufacturerNameArg): string[] | undefined;

  /**
   * Validate if a model is valid for a manufacturer.
   */
  isValidModel(
    manufacturerName: ManufacturerNameArg,
    options: { modelNumber?: string | null }
  ): boolean;
}

/**
 * Result of a robot operation execution
 */
export interface OperationResult {
  success: boolean;
  error?: string;
  data?: any;
}

/**
 * Robot operation execution interface
 * Abstracts robot operations without assuming script-based execution
 * Different manufacturers can implement this using scripts, APIs, or other protocols
 *
 * Note: This interface is for IMPERATIVE operations (execute once, get result).
 * For DECLARATIVE bindings (subscriptions, event listeners), use manufacturer-specific hooks.
 */
export interface RobotOperations {
  /** Execute move-to-point operation */
  moveToPoint(
    machineId: string,
    point: Waypoint,
    qnear: number[],
    stickout: number,
    context: RobotOperationalContext & ImplementationContext
  ): Promise<OperationResult>;

  /** Start fine-tune mode */
  startFineTune(
    machineId: string,
    context: RobotOperationalContext & {
      onStop?: () => void;
    } & ImplementationContext
  ): Promise<OperationResult>;

  /** Move to position during fine-tune mode */
  fineTuneMoveTo(machineId: string, pose: Pose): Promise<OperationResult>;

  /** Stop/halt robot operations */
  halt(machineId: string, operationName?: string): Promise<OperationResult>;

  /** Stop program execution */
  stopProgram(machineId: string): Promise<OperationResult>;

  /** Close and cleanup robot operations resources */
  close(): void;
}

/**
 * Robot kinematics interface
 * Handles forward/inverse kinematics calculations for different robot types
 */
export interface RobotKinematics {
  /** Calculate forward kinematics (joint space -> tool space) */
  getForwardKinematics(jointPositions: QNear, tcp: Pose): Pose;

  /** Calculate inverse kinematics (tool space -> joint space) */
  getInverseKinematics(desiredPose: Pose, tcpOffset: Pose): Array<QNear>;

  /** Check if a pose is reachable by the robot */
  isWaypointReachable(pose: Pose, tcpOffset: Pose): boolean;

  /** Get DH parameters for this robot type */
  getDHParameters(): Array<JointDHParameters>;
}
